import debounce from 'debounce-promise'
import React, { useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import ExploreReview from './ExploreReview'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import IconButton from 'LuxKit/Button/IconButton'
import Carousel from 'LuxKit/Carousel/Carousel'
import type { ScrollPosition } from 'LuxKit/Carousel/utils'
import {
  getElementContentWidth,
  getHorizontalScrollPosition,
} from 'LuxKit/Carousel/utils'
import LineAngleLeftBIcon from 'LuxKit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import type { ExploreItemReview } from 'tripPlanner/types/common'

interface Props {
  rating: number | undefined
  ratingsCount: number | undefined
  reviews: Array<ExploreItemReview> | undefined
}

function ExploreReviews({ rating, ratingsCount, reviews }: Props) {
  const intl = useIntl()
  const [position, setPosition] = useState<ScrollPosition>('start')
  const carouselRef = useRef<HTMLDivElement | null>(null)

  /** Based on logic from CardCarousel */
  const onCarouselArrowClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!carouselRef.current) {
      return
    }
    const direction = e.currentTarget.dataset.direction
    const contentWidth = getElementContentWidth(carouselRef.current)

    const nextLeft =
      direction === 'left' ?
        carouselRef.current.scrollLeft - contentWidth :
        carouselRef.current.scrollLeft + contentWidth

    carouselRef.current.scrollTo({
      left: nextLeft,
      behavior: 'smooth',
    })
  }

  const onScroll = useMemo(() => {
    return debounce(() => {
      if (carouselRef.current) {
        setPosition(getHorizontalScrollPosition(carouselRef.current))
      }
    }, 32)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef.current])

  const showReviews = reviews && reviews.length > 0
  const showCarousel = reviews && reviews.length > 1

  return (
    <VerticalSpacer gap={16}>
      <Group direction="horizontal" horizontalAlign="space-between">
        <VerticalSpacer gap={4}>
          {showReviews && (
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'Xgdc-EMbdeb1-AzG_YA2',
                defaultMessage: 'Reviews',
                description: 'Heading for the reviews section of an explore item',
              })}
            </BodyText>
          )}
          {!!rating && (
            <GooglePlaceRatingDisplay
              rating={rating}
              ratingsCount={ratingsCount}
              size="S"
            />
          )}
        </VerticalSpacer>
        {showCarousel && (
          <div>
            <IconButton
              kind="tertiary"
              size="medium"
              shape="square"
              onClick={onCarouselArrowClick}
              data-direction="left"
              disabled={position === 'start'}
            >
              <LineAngleLeftBIcon />
            </IconButton>
            <IconButton
              kind="tertiary"
              size="medium"
              shape="square"
              onClick={onCarouselArrowClick}
              data-direction="right"
              disabled={position === 'end'}
            >
              <LineAngleRightBIcon />
            </IconButton>
          </div>
        )}
      </Group>

      {showReviews && reviews.length === 1 && <ExploreReview review={reviews[0]} />}

      {showCarousel && (
        <div>
          <Carousel
            snap="start"
            gutterSize={0}
            width="100%"
            ref={carouselRef}
            onScroll={onScroll}
          >
            {reviews.map((review) => (
              <div key={`${review.authorUrl}-${review.publishTime}`}>
                <ExploreReview review={review} />
              </div>
            ))}
          </Carousel>
        </div>
      )}
    </VerticalSpacer>
  )
}

export default ExploreReviews
